import { useState } from "react"
import { Copy, Check } from "lucide-react"
import { cn } from "@/lib/cn"
import { toneCss } from "../format"
import type { KeyValueBlock, KeyValueItem } from "../types"

// Two-column fact list. `columns: 2` reflows to 1-col at xs/sm via
// container queries on the parent — no JS layout switching, the CSS
// handles it.
export function KeyValueBlockView({ block }: { block: KeyValueBlock }) {
  const cols = block.columns === 2 ? 2 : 1
  const items = Array.isArray(block.items) ? block.items : []
  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-3 valet-uidoc-keyvalue",
        cols === 2 && "valet-uidoc-keyvalue-2col",
      )}
    >
      {items.map((item, idx) => (
        <KvRow key={`${item.label}-${idx}`} item={item} />
      ))}
    </dl>
  )
}

function KvRow({ item }: { item: KeyValueItem }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-app-subtle">{item.label}</dt>
      <dd className="mt-1 min-w-0 text-sm text-app-text">
        <KvValue item={item} />
      </dd>
    </div>
  )
}

function KvValue({ item }: { item: KeyValueItem }) {
  if (item.redact) {
    return <span className="text-app-subtle">&lt;redacted&gt;</span>
  }
  if (item.value === null || item.value === undefined || item.value === "") {
    return <span className="text-app-subtle">—</span>
  }

  const display =
    typeof item.value === "boolean"
      ? item.value ? "Yes" : "No"
      : String(item.value)

  if (item.badge) {
    const css = toneCss(item.badge)
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-chip border px-1.5 py-0.5 text-[11px] font-medium",
          css.text,
          css.bg,
          css.border,
        )}
      >
        <span className={cn("h-1.5 w-1.5 rounded-full", css.dot)} aria-hidden="true" />
        {display}
      </span>
    )
  }

  if (item.copyable) {
    return <CopyableInline value={display} mono={item.mono ?? false} />
  }

  return (
    <span className={cn("break-words", item.mono && "font-mono text-[12px]")}>{display}</span>
  )
}

function CopyableInline({ value, mono }: { value: string; mono: boolean }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : "Click to copy"}
      className={cn(
        "group inline-flex max-w-full items-center gap-1 rounded-control border bg-black/30 px-1.5 py-0.5 transition",
        copied
          ? "border-status-success/40 text-status-success"
          : "border-border hover:border-brand-red/40 hover:bg-black/40",
        mono && "font-mono text-[11px]",
      )}
    >
      <span className="truncate text-left">{value}</span>
      {copied
        ? <Check size={12} strokeWidth={2.5} className="shrink-0" />
        : <Copy size={12} strokeWidth={2} className="shrink-0 text-app-muted group-hover:text-app-text" />
      }
    </button>
  )
}
